import purchaseService from '../services/purchaseService';

/**
 * Purchase content action (without dispatch)
 * @param {string} contentId - Content ID to purchase
 * @param {Object} purchasePayload - Purchase payload containing amount and returnUrl
 * @returns {Promise<Object>} Purchase result
 */
export const purchaseContent = async (contentId, purchasePayload) => {
  try {
    if (!contentId) {
      return { success: false, error: 'Content ID is required' };
    }

    if (!purchasePayload || !purchasePayload.amount) {
      return { success: false, error: 'Purchase amount is required' };
    }

    const response = await purchaseService.purchaseContent(contentId, purchasePayload);
    
    if (response.success) {
      return { success: true, data: response.data };
    } else {
      return { success: false, error: response.message || 'Failed to purchase content' };
    }
  } catch (error) {
    const errorMessage = error?.response?.data?.message || error?.message || 'An unexpected error occurred while purchasing content';
    return { success: false, error: errorMessage };
  }
};

/**
 * Purchase message content (DM/chat PPV) – calls POST /messages/:dest/purchase_content
 * @param {string} creatorId - Creator user id (dest), e.g. creatorInfo._id
 * @param {Object} purchasePayload - Purchase payload with amount, returnUrl, and content_id
 * @returns {Promise<Object>} Purchase result
 */
export const purchaseMessageContent = async (creatorId, purchasePayload) => {
  try {
    if (!creatorId) {
      return { success: false, error: 'Creator ID is required' };
    }
    if (!purchasePayload?.content_id) {
      return { success: false, error: 'Content ID is required' };
    }
    if (!purchasePayload?.amount) {
      return { success: false, error: 'Purchase amount is required' };
    }
    const response = await purchaseService.purchaseMessageContent(creatorId, purchasePayload);
    if (response.success) {
      return { success: true, data: response.data };
    }
    return { success: false, error: response.message || 'Failed to purchase message content' };
  } catch (error) {
    const errorMessage = error?.response?.data?.message || error?.message || 'An unexpected error occurred while purchasing message content';
    return { success: false, error: errorMessage };
  }
};

/**
 * Check if content has been purchased by the current user (without dispatch)
 * @param {string} contentId - Content ID to check
 * @returns {Promise<Object>} Purchase check result with purchased status
 */
export const checkContentPurchase = async (contentId) => {
  try {
    if (!contentId) {
      return { success: false, error: 'Content ID is required', purchased: false };
    }

    const response = await purchaseService.checkContentPurchase(contentId);

    if (response.success) {
      const purchased = response.status === 200 || response.data?.purchased === true;
      return {
        success: true,
        data: response.data,
        purchased,
        status: response.status,
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to check content purchase',
      purchased: false,
    };
  } catch (error) {
    const errorMessage = error?.response?.data?.message || error?.message || 'An unexpected error occurred while checking content purchase';
    return { success: false, error: errorMessage, purchased: false };
  }
};

/**
 * Cancel/delete a purchase action (without dispatch)
 * @param {string} purchaseId - Purchase ID to cancel
 * @param {boolean} isManualRemove - Whether to manually remove the purchase (true) or cancel the purchase (false)  
 * @returns {Promise<Object>} Cancel purchase result
 */
export const cancelPurchase = async (purchaseId, isManualRemove=false) => {
  try {
    if (!purchaseId) {
      return { success: false, error: 'Purchase ID is required' };
    }

    const response = await purchaseService.cancelPurchase(purchaseId, isManualRemove);
    
    if (response.success) {
      return { success: true, data: response.data };
    } else {
      return { success: false, error: response.message || 'Failed to cancel purchase' };
    }
  } catch (error) {
    const errorMessage = error?.response?.data?.message || error?.message || 'An unexpected error occurred while cancelling the purchase';
    return { success: false, error: errorMessage };
  }
};

/**
 * Cancel/delete a message content purchase action (without dispatch)
 * Uses DELETE /messages/purchase_content/:purchaseId/:isManualRemove
 * @param {string} purchaseId - Message purchase content ID to cancel
 * @param {boolean} isManualRemove - Whether to manually remove the purchase (true) or cancel the purchase (false)
 * @returns {Promise<Object>} Cancel purchase result
 */
export const cancelMessagePurchase = async (purchaseId, isManualRemove=false) => {
  try {
    if (!purchaseId) {
      return { success: false, error: 'Purchase ID is required' };
    }

    const response = await purchaseService.cancelMessagePurchase(purchaseId, isManualRemove);
    
    if (response.success) {
      return { success: true, data: response.data };
    } else {
      return { success: false, error: response.message || 'Failed to cancel purchase' };
    }
  } catch (error) {
    const errorMessage = error?.response?.data?.message || error?.message || 'An unexpected error occurred while cancelling the purchase';
    return { success: false, error: errorMessage };
  }
};

